import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {z} from 'zod';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {ContentCardScratchpadState} from '../../scratchpad/contentCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {NodeOutputData, NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {ContentSelectionStrategyValue} from 'common/src/modules/contentFeeds/contentFeeds.enums';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';

const inputSchema = z.object({
  strategy: z.nativeEnum(ContentSelectionStrategyValue).describe('The content selection strategy'),
});

export default function setContentStrategyTool() {
  async function execute(
    params: ToolExecuteParams<typeof inputSchema, ContentCardScratchpadState>,
  ): Promise<ToolResult> {
    const {
      input: {strategy},
      toolUtils: {agentScratchpad},
    } = params;
    const validatedScratchpad = validateAgentScratchpad<ContentCardScratchpadState>(
      agentScratchpad,
      NodeType.CONTENT_FEED,
    );

    validatedScratchpad.card.strategy = strategy;

    // Rebuild outputs with correct ordering: Success, Partial Success (if custom matching), Fallback
    const fallbackOutput = validatedScratchpad.card.outputs.find(
      (o) => o.type === OutputType.Fallback,
    );
    const successOutput = validatedScratchpad.card.outputs.find(
      (o) => o.type === OutputType.Regular,
    );

    const newOutputs: NodeOutputData[] = [];

    // Add Success output (always present)
    if (successOutput) {
      newOutputs.push(successOutput);
    } else {
      newOutputs.push({
        id: 1,
        name: 'Success',
        type: OutputType.Regular,
        nodeId: NO_NODE_ID,
      });
    }

    // Add Fallback output (always present)
    if (fallbackOutput) {
      newOutputs.push({...fallbackOutput, id: newOutputs.length + 1});
    } else {
      newOutputs.push({
        id: newOutputs.length + 1,
        name: 'Fallback',
        type: OutputType.Fallback,
        nodeId: NO_NODE_ID,
      });
    }

    validatedScratchpad.card.outputs = newOutputs;

    return toolResult(`Content selection strategy set to ${strategy}, and outputs rebuilt`);
  }

  return tool({
    name: V2CardAgentToolName.SetContentStrategy,
    displayName: 'Setting content strategy',
    description: 'Set the content selection strategy (Specific or Matching)',
    inputSchema,
    execute,
  });
}
